import type { RoleBadgeTone } from "../../components/atoms/RoleBadge";
import type { WorkspaceInitialsBadgeTone } from "../../components/atoms/WorkspaceInitialsBadge";
import type { MembershipRead } from "./tenantContext.types";

/**
 * Tenant memberships → the organization picker's options (auth spec §4
 * "Organization combobox", §5 C1–C3). Pure: no React, no storage.
 */

/** Avatar fills rotate in this order across the user's organizations (spec §4). */
export const ORG_AVATAR_TINTS = [
  "lilac",
  "mist",
  "blush",
] as const satisfies readonly WorkspaceInitialsBadgeTone[];

export type OrgAvatarTint = (typeof ORG_AVATAR_TINTS)[number];

/** Spec §4: hide the menu's search below 5 organizations. */
export const ORG_SEARCH_MIN_COUNT = 5;

/** Same placeholder `getWorkspaceInitials` uses, for a name with no letters or digits. */
export const ORG_INITIALS_FALLBACK = "?";

/** One organization as the combobox renders it — every display value precomputed. */
export interface OrgOptionViewModel {
  /** `tenant_id`, what `select-tenant` takes. */
  id: string;
  name: string;
  initials: string;
  /** "Admin", "Member", … — never the raw role key. */
  roleLabel: string;
  roleTone: RoleBadgeTone;
  tint: OrgAvatarTint;
}

/** Spec §5: the known role keys and their labels. */
const ROLE_LABELS: Readonly<Record<string, string>> = {
  tenant_admin: "Admin",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

const titleCase = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/**
 * Spec §5: known keys map to their label; anything else is title-cased with
 * underscores as spaces ("billing_manager" → "Billing Manager"). Never
 * surfaces a raw key; a missing role gives "".
 */
export const formatRoleLabel = (role: string | null | undefined): string => {
  const key = role?.trim() ?? "";
  if (!key) return "";
  const known = ROLE_LABELS[key.toLowerCase()];
  if (known) return known;
  return key.split(/[_\s]+/).filter(Boolean).map(titleCase).join(" ");
};

/** Prototype `roleClass`: any admin role key is the purple badge, the rest ink. */
export const getRoleTone = (role: string | null | undefined): RoleBadgeTone =>
  /admin/i.test(role ?? "") ? "primary" : "ink";

/** First letter or digit, so "(EU)" gives "E", not "(". */
const firstAlphanumeric = (word: string): string => word.match(/[\p{L}\p{N}]/u)?.[0] ?? "";

/**
 * Spec §5: first letters of the first two words after removing a possessive
 * "'s" ("Hardik's Workspace" → "HW"). Words split on spaces, `_` and `-`, as
 * in the prototype. Gives "" when nothing is left, so callers choose the
 * fallback. Also used for the signed-in user's name.
 */
export const getOrgInitials = (name: string): string =>
  name
    .replace(/['’]s\b/i, "")
    .split(/[\s_-]+/)
    .map(firstAlphanumeric)
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

/**
 * The tint comes from the organization's position in the full list, so an
 * avatar keeps its colour while the search narrows the list.
 */
export const toOrgOptions = (
  memberships: readonly MembershipRead[] | null | undefined,
): OrgOptionViewModel[] =>
  (memberships ?? []).map((membership, index) => ({
    id: membership.tenant_id,
    name: membership.tenant_name,
    initials: getOrgInitials(membership.tenant_name) || ORG_INITIALS_FALLBACK,
    roleLabel: formatRoleLabel(membership.role_name),
    roleTone: getRoleTone(membership.role_name),
    tint: ORG_AVATAR_TINTS[index % ORG_AVATAR_TINTS.length],
  }));

/** The name of the organisation `tenantId` among the caller's memberships; null when it isn't one. */
export const activeTenantName = (
  memberships: readonly MembershipRead[] | null | undefined,
  tenantId: string | null,
): string | null =>
  (tenantId && memberships?.find((membership) => membership.tenant_id === tenantId)?.tenant_name?.trim()) || null;

/** Case-insensitive substring match on the name; a blank query keeps everything. */
export const filterOrgOptions = (
  options: readonly OrgOptionViewModel[],
  query: string,
): OrgOptionViewModel[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...options];
  return options.filter((option) => option.name.toLowerCase().includes(needle));
};

/** Spec §5 recommendation: search only pays off from 5 organizations up. */
export const shouldShowOrgSearch = (count: number): boolean => count >= ORG_SEARCH_MIN_COUNT;
